import { computed, effect, Injectable, signal, inject } from '@angular/core';
import { OutletAction, OutletActionResult, PowerDeviceDto, PowerHistorySample } from './power';
import { RoomUsage } from './room-usage';
import { SensorsState, ShellyDeviceRequest, ShellyPairRequest } from './sensors';
import { MqttMonitorResponse } from './mqtt';
import { Router } from '@angular/router';
import { JsonRpcWebSocketService } from './jsonrpc-websocket.service';
import { timer, Subscription } from 'rxjs';
import { Message, parseMessages, filterMessagesByLevel } from './messages';
import { environment } from 'src/environments/environment';
import { LogEntry } from './logs';
import { SystemMonitorStat } from './system-monitor-stat';
import { CresnetDevice } from './cresnet-device';
import { MessageService } from 'primeng/api';
import { HttpClient } from '@angular/common/http';
import { PING_LIMITS, PingRequest, PingResult } from './ping.service.interface';
import { BacnetCommandResult, BacnetDiscoveryResponse, BacnetLicenseApplyResult, BacnetLicenseInfo, BacnetNetworkApplyResult, BacnetNetworkPreview, BacnetNetworkRequest, BacnetNetworkResponse, BacnetStatusResponse } from './bacnet';

/** One room on the Room ratings page: its own setting (`override`, null follows the default) and the result. */
export interface DeviceFeedbackRoom {
  id: number;
  uniqueId: string;
  name: string;
  override: boolean | null;
  available: boolean;
}

/** `feedbackSettingsGet`: the ratings setting in force on this processor and where it came from. */
export interface DeviceFeedbackSettings {
  source: 'cloud' | 'local' | 'default';
  managedByCloud: boolean;
  cloudReceivedUtc: string | null;
  /** The default for the system and every room without an override. */
  enabled: boolean;
  /** Whether feedback about the system as a whole (no room) is accepted. */
  systemAvailable: boolean;
  rooms: DeviceFeedbackRoom[];
}

export interface PropertyListItem {
  key: string;
  value: any;
  type: string;
}

export interface CredentialItem {
  key: string;
  hasValue: boolean;
  valueLength: number;
}

// File Explorer types
export interface FileExplorerEntry {
  name: string;
  path: string;
  type: 'file' | 'directory';
  isSymlink?: boolean;
  extension?: string;
  sizeBytes?: number;
  modifiedUtc: string;
  createdUtc?: string;
  canPreview: boolean;
  previewType?: 'text' | 'image' | 'pdf' | 'none';
}

export interface FileExplorerListResponse {
  success: boolean;
  error?: string;
  path?: string;
  rootDirectory?: string;
  entries?: FileExplorerEntry[];
}

export interface FileExplorerFileInfo {
  success: boolean;
  error?: string;
  path?: string;
  name?: string;
  extension?: string;
  sizeBytes?: number;
  modifiedUtc?: string;
  createdUtc?: string;
  canPreview?: boolean;
  previewType?: 'text' | 'image' | 'pdf' | 'none';
  language?: string;
  mimeType?: string;
  binaryWarning?: string;    // Warning if file may contain binary data
}

export interface FileExplorerReadResponse extends FileExplorerFileInfo {
  content?: string;           // For text files
  contentBase64?: string;     // For binary files (images, PDFs)
  isTruncated?: boolean;
  truncatedAt?: number;
  warning?: string;           // For binary detection warnings
  isBinary?: boolean;         // Detected as binary
  binaryDetectionConfidence?: 'high' | 'medium' | 'low';
}

// Device Command types
export interface DeviceCommandArgument {
  name: string;
  label: string;
  description: string;
  type: 'String' | 'Bool' | 'Int' | 'Double' | 'Select';
  required: boolean;
  defaultValue?: any;
  options?: string[];  // For Select type
  min?: number;        // For Int/Double
  max?: number;        // For Int/Double
}

export interface DeviceCommand {
  name: string;
  description: string;
  category: string;
  arguments: DeviceCommandArgument[];
}

export interface DeviceCommandResult {
  success: boolean;
  message: string;
  data?: any;
  errorCode?: string;
}

// Assembly/Package info types
export interface AssemblyInfo {
  name: string;
  version: string;
  fullName: string;
  location?: string;
  isSystemAssembly: boolean;
  projectUrl?: string;
}

export interface AssembliesResponse {
  assemblies: AssemblyInfo[];
  count: number;
  appName?: string;
  appVersion?: string;
  coreLibName?: string;
  coreLibVersion?: string;
  targetFramework?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private wsUrl: string = '';
  private reconnectAttempts = 0;
  private reconnectSub: Subscription | null = null;
  private statusPollSub: Subscription | null = null;
  private notificationsSub: Subscription | null = null;
  connectionStatus = signal<'connected' | 'disconnected' | 'connecting'>('connecting');
  statusData: any = signal(null);
  private lastAppAssemblyVersion: string | null = null;
  systemMonitor = computed(() => this.statusData()?.SystemMonitor ?? null);
  systemMonitorHistory = signal<SystemMonitorStat[]>([]);
  loggedInUser = signal<string | null>(null);
  // Increments when the device's ConfigChanged event fires (from any source)
  configChangedCounter = signal(0);
  // All messages fetched from backend
  messages = signal<Message[]>([]);
  // Computed subset of error & warning messages
  errorAndWarningMessages = computed(() => filterMessagesByLevel(this.messages(), ['error', 'warn']));
  // Count for badge usage
  errorCount = computed(() => this.messages().filter(m => m.level === 'error').length);
  warningCount = computed(() => this.messages().filter(m => m.level === 'warn').length);
  initialized = signal<boolean>(false);
  loggerHistory = signal<LogEntry[]>([]);
  cloudInitialized = computed(() => this.statusData()?.cloudInitialized ?? false);
  cloudRegistered = computed(() => this.statusData()?.cloudRegistered ?? false);
  cloudDeviceId = computed(() => this.statusData()?.cloudDeviceId ?? null);
  cloudLastCheckIn = computed(() => this.statusData()?.cloudCheckedIn ?? null);
  cloudHost = computed(() => this.statusData()?.cloudHost ?? 'proav.cloud');
  cloudPortalBaseUrl = computed(() => this.statusData()?.cloudPortalBaseUrl ?? 'https://proav.cloud');
  configIsDefault = computed(() => this.statusData()?.configIsDefault === true);
  // Messages are fetched on connect and updated via events only (no polling)
  private messageService = inject(MessageService);
  private httpClient = inject(HttpClient);
  private router = inject(Router);

  constructor(public wsService: JsonRpcWebSocketService) {
    // Guard: Skip initialization if running in cloud mode (which uses CloudApiService instead)
    // Cloud app sets this flag in main.cloud.ts before bootstrap
    if ((window as any).__CLOUD_MODE__ === true) {
      if (!environment.production) {
        console.log('ApiService: Skipping initialization in cloud mode');
      }
      return;
    }

    this.initialized.set(true);

    if (!environment.production) {
      console.log('ApiService initialized');
    }
    // Use direct WebSocket URL from environment if set, otherwise use proxy
    if (environment.wsUrl) {
      this.wsUrl = environment.wsUrl;
    } else {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = window.location.host;
      this.wsUrl = `${wsProtocol}//${wsHost}/ws`;
    }
    effect(() => {
      if (!environment.production) {
        // console.log('Status changed:', this.statusData(), this.connectionStatus());
      }
    });

    // Connect immediately without delay
    this.connectWithRetry();
    // effect(() => {
    //   console.log('Messages changed:', this.messages());
    //   console.log('Error/Warning messages:', this.errorAndWarningMessages());
    // });
  }

  /**
   * Handle status response - updates state and checks for version changes
   */
  private handleStatusResponse(response: any) {
    this.statusData.set(response);

    // Update page title
    if (response.controllerPromptName && response.network?.ipAddress) {
      document.title = `${response.controllerPromptName}@${response.network.ipAddress} (App ${response.applicationNumber})`;
    }

    // Check AppAssemblyVersion and reload if changed (app was updated)
    const newVersion = response?.appAssemblyVersion ?? null;
    if (this.lastAppAssemblyVersion !== null && this.lastAppAssemblyVersion !== newVersion) {
      // Skip the reload if we're on the reconnecting page — it already handles
      // the post-update navigation cleanly. Reloading would lose the router state
      // and show the update animations a second time.
      if (this.router.url === '/reconnecting') {
        console.log(`[ApiService] App version changed (${this.lastAppAssemblyVersion} → ${newVersion}), reconnecting page will handle navigation`);
      } else {
        console.log(`[ApiService] App version changed from ${this.lastAppAssemblyVersion} to ${newVersion}, reloading...`);
        window.location.reload();
      }
    }
    this.lastAppAssemblyVersion = newVersion;
  }

  private connectWithRetry() {
    this.connectionStatus.set('connecting');

    // Set up connection monitoring callbacks BEFORE connecting
    // so they're ready for any reconnection events
    this.setupConnectionMonitor();

    this.wsService.connect(this.wsUrl)
      .then(() => {
        // Initial status and messages fetch on connect
        this.wsService.sendRequest('statusGet').then((response) => {
          this.handleStatusResponse(response);
        }).catch(e => console.error('Initial statusGet failed', e));
        this.systemMonitorHistoryGet(true).then(history => {
          this.systemMonitorHistory.set(history);
        }).catch(e => console.error('Initial systemMonitorHistoryGet failed', e));
        this.fetchMessages();
        this.connectionStatus.set('connected');
        this.reconnectAttempts = 0;
        this.attachNotificationListener();
        this.getAuthStatus().then((auth) => {
          if (auth) {
            this.loggedInUser.set(auth.name || null);
            if (this.loggerHistory().length > 0) {
              this.loggerStart();
            }
          } else {
            // Server doesn't recognize our session - clear stored token
            localStorage.removeItem('sessionToken');
            localStorage.removeItem('sessionExpiry');
            this.loggedInUser.set(null);
          }
        }, (error) => {
          console.error('authStatus error', error);
          this.loggedInUser.set(null);
        });
        this.startStatusAndMessagesPolling();
      })
      .catch((err: any) => {
        this.connectionStatus.set('disconnected');
        this.stopStatusAndMessagesPolling();
        this.scheduleReconnect();
      });
  }

  private setupConnectionMonitor() {
    // Monitor SignalR connection for reconnecting events
    this.wsService.onReconnectingCallback = () => {
      console.log('[ApiService] SignalR reconnecting...');
      this.connectionStatus.set('connecting');
      this.stopStatusAndMessagesPolling();
    };

    // Monitor SignalR connection for reconnected events
    this.wsService.onReconnectedCallback = () => {
      console.log('[ApiService] SignalR reconnected');
      this.connectionStatus.set('connected');
      this.reconnectAttempts = 0;
      this.wsService.setUpdateInProgress(false);
      this.startStatusAndMessagesPolling();
      // Re-fetch status after reconnect (will reload if version changed)
      this.wsService.sendRequest('statusGet').then((response) => {
        this.handleStatusResponse(response);
      }).catch(e => console.error('statusGet failed after reconnect', e));
    };

    // Monitor SignalR connection for close events (permanent disconnect)
    this.wsService.onCloseCallback = () => {
      console.log('[ApiService] SignalR connection closed');
      this.connectionStatus.set('disconnected');
      this.stopStatusAndMessagesPolling();
      this.detachNotificationListener();
      this.scheduleReconnect();
    };
  }

  private attachNotificationListener() {
    this.detachNotificationListener();
    this.notificationsSub = this.wsService.notifications$.subscribe(note => {
      // Expecting method formats like "EventService:ConfigChanged", etc.
      if (note.method.startsWith('EventService:')) {
        const eventName = note.method.substring('EventService:'.length);
        console.debug(`[Notification] EventService:${eventName}`, note.params);
        switch (eventName) {
          case 'ConfigChanged':
            // Re-fetch status/config as needed
            this.wsService.sendRequest('statusGet', undefined, this.getStoredToken())
              .then(r => this.handleStatusResponse(r))
              .catch(e => console.error('Failed to refresh status after ConfigChanged', e));
            this.configChangedCounter.update(c => c + 1);
            break;
          case 'SystemMonitorStatsChange': {
            // Append new stat immutably so dependent signals/components update
            const stat = note.params as SystemMonitorStat;
            if (!environment.production) {
              console.debug('SystemMonitorStatsChange event', stat);
            }
            this.systemMonitorHistory.update(list => {
              const next = [...list, stat];
              return next;
            });
            this.statusData.update((sd: any) => {
              return { ...sd, SystemMonitor: stat };
            });
            break;
          }
          case 'UpdateAvailableChange':
            if (!environment.production) {
              console.debug('UpdateAvailableChange event', note.params);
            }
            this.fetchMessages();
            break;
          case 'DiagnosticsMessagesUpdated':
            if (!environment.production) {
              console.log('DiagnosticsMessagesUpdated event', note.params);
            }
            var publisherId = (note.params as any).publisherId;
            // replace messages from that publisher
            var newMessages = parseMessages((note.params as any).messages || []);
            this.messages.update(list => {
              const filtered = list.filter(m => m.publisherId !== publisherId);
              const next = [...filtered, ...newMessages];
              return next;
            });

            // this.scheduleMessagesRefresh();
            break;
          // (Duplicate case removed - logic handled above)
          case 'LogEntry':
            console.debug('LogEntry event received', note.params, 'history length:', this.loggerHistory().length);
            if (this.loggerHistory().length > 0) {
              this.loggerHistory.update(list => {
                if (!environment.production) {
                  console.log('Appending log entry', note.params);
                }
                const next = [...list, note.params as LogEntry];
                return next;
              });
            } else {
              console.debug('LogEntry ignored - loggerHistory is empty (not initialized yet)');
            }
            break;
          case 'ProgramStopping':
            // Server is shutting down - force disconnect to trigger reconnection loop
            console.log('[ApiService] Received ProgramStopping notification, forcing disconnect');
            this.forceDisconnect();
            break;
          case 'updateStarting':
            // Software update is being applied — navigate ALL connected dashboards to the update screen
            console.log('[ApiService] Received updateStarting notification, navigating to update screen');
            this.wsService.setUpdateInProgress(true);
            this.router.navigate(['/reconnecting'], {
              state: { action: 'update' },
              replaceUrl: true
            });
            // Force disconnect so we don't stay connected to the old process
            this.forceDisconnect();
            break;
          case 'restartStarting':
            // App restart — navigate ALL connected dashboards to the reconnecting screen
            console.log('[ApiService] Received restartStarting notification, navigating to reconnecting screen');
            this.router.navigate(['/reconnecting'], {
              state: { action: 'restart', returnUrl: this.reconnectReturnUrl() },
              replaceUrl: true
            });
            this.forceDisconnect();
            break;
          case 'rebootStarting':
            // Appliance reboot — navigate ALL connected dashboards to the reconnecting screen
            console.log('[ApiService] Received rebootStarting notification, navigating to reconnecting screen');
            this.router.navigate(['/reconnecting'], {
              state: { action: 'reboot', returnUrl: this.reconnectReturnUrl() },
              replaceUrl: true
            });
            this.forceDisconnect();
            break;
          case 'firmwareUpdateStarting':
            // Firmware update — device will reboot after PUF completes
            console.log('[ApiService] Received firmwareUpdateStarting notification, navigating to reconnecting screen');
            this.wsService.setUpdateInProgress(true);
            this.router.navigate(['/reconnecting'], {
              state: { action: 'firmware' },
              replaceUrl: true
            });
            this.forceDisconnect();
            break;
          default:
            // Generic handling/log
            if (!environment.production) {
              console.debug('Unhandled EventService notification', eventName, note.params);
            }
            break;
        }
      } else {
        // Other notification types can be handled here in future
        if (!environment.production) {
          console.debug('Notification received', note.method, note.params);
        }
      }
    });
  }

  private detachNotificationListener() {
    if (this.notificationsSub) {
      this.notificationsSub.unsubscribe();
      this.notificationsSub = null;
    }
  }

  private scheduleReconnect() {
    this.reconnectAttempts++;
    const delay = 5000; // fixed 5 seconds delay between reconnect attempts
    if (this.reconnectSub) {
      this.reconnectSub.unsubscribe();
    }
    this.reconnectSub = timer(delay).subscribe(() => {
      this.connectWithRetry();
    });
  }

  private startStatusAndMessagesPolling() {
    this.stopStatusAndMessagesPolling();
    // Poll status only every 60s (messages updated via events)
    this.statusPollSub = timer(60000, 60000).subscribe(() => {
      if (this.connectionStatus() === 'connected') {
        this.wsService
          .sendRequest('statusGet')
          .then((response) => {
            this.handleStatusResponse(response);
          })
          .catch((e: any) => console.error('statusGet polling error', e));
      }
    });
  }

  private stopStatusAndMessagesPolling() {
    if (this.statusPollSub) {
      this.statusPollSub.unsubscribe();
      this.statusPollSub = null;
    }
  }

  private getStoredToken(): string | null {
    const token = localStorage.getItem('sessionToken');
    const expiry = localStorage.getItem('sessionExpiry');
    if (token && expiry) {
      const expiryDate = new Date(expiry);
      if (expiryDate > new Date()) {
        return token;
      } else {
        // Token expired
        localStorage.removeItem('sessionToken');
        localStorage.removeItem('sessionExpiry');
        this.loggedInUser.set(null);
      }
    }
    return null;
  }

  private getAuthHeaders(): any {
    const token = this.getStoredToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  async login(username: string, password: string, rememberMe: boolean): Promise<any> {
    let response = await this.wsService.sendRequest('login', { username, password, rememberMe });
    if (!environment.production) {
      console.log('Login response:', response);
    }
    // Response is camelCase from SignalR: { name: "guest", sessionId: "...", expiryTime: "..." }
    if (response && response.sessionId) {
      // Store the session token for future requests
      const token = response.sessionId;
      // store the token in localStorage or a cookie for persistence and also note expiry
      localStorage.setItem('sessionToken', token);
      localStorage.setItem('sessionExpiry', response.expiryTime);
      this.loggedInUser.set(response?.name || null);
      return response;
    }
    throw new Error('Login failed: ' + (response?.error?.message || 'Unknown error'));
  }

  async logout(): Promise<void> {
    try {
      await this.wsService.sendRequest('logout', undefined, this.getStoredToken());
    } catch (e) {
      console.error('Error during logout request:', e);
    }
    // Clear stored token and user info
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('sessionExpiry');
    this.loggedInUser.set(null);
  }

  async getAuthStatus(): Promise<any> {
    const response = await this.wsService.sendRequest('authStatus', undefined, this.getStoredToken());
    // console.log('Auth status response:', response);
    return response;
  }

  async consoleCommand(command: string): Promise<string> {
    const response = await this.wsService.sendRequest('consoleCmd', { command }, this.getStoredToken());
    if (!environment.production) {
      console.log('Console command response:', response);
    }
    return response;
  }

  async configGet(): Promise<any> {
    const response = await this.wsService.sendRequest('configGet', undefined, this.getStoredToken());
    if (!environment.production) {
      console.log('Config get response:', response);
    }
    return response;
  }

  async configSet(config: any): Promise<void> {
    let response = await this.wsService.sendRequest('configSet', { config }, this.getStoredToken());
    if (!environment.production) {
      console.log('Config set completed', response);
    }
  }

  async configLoad(filePath: string): Promise<any> {
    const response = await this.wsService.sendRequest('configLoad', { filePath }, this.getStoredToken());
    if (!environment.production) {
      console.log('Config load completed');
    }
    return response;
  }

  async configNew(fileName: string): Promise<any> {
    const response = await this.wsService.sendRequest('configNew', { fileName }, this.getStoredToken());
    if (!environment.production) {
      console.log('Config new completed');
    }
    return response;
  }

  async configDelete(): Promise<any> {
    const response = await this.wsService.sendRequest('configDelete', undefined, this.getStoredToken());
    if (!environment.production) {
      console.log('Config delete completed');
    }
    return response;
  }

  async templateGet(): Promise<any> {
    return await this.wsService.sendRequest('templateGet', undefined, this.getStoredToken());
  }

  async templateApply(templateId: string, fieldValues: Record<string, any>): Promise<any> {
    return await this.wsService.sendRequest('templateApply', { templateId, fieldValues }, this.getStoredToken());
  }

  async fetchMessages(): Promise<void> {
    try {
      if (!environment.production) {
        console.log('Fetching messages from backend');
      }
      const response = await this.wsService.sendRequest('messagesGet');
      const parsed = parseMessages(response);
      if (!environment.production) {
        console.log('Fetched messages:', parsed);
      }
      this.messages.set(parsed);
    } catch (e: any) {
      this.messageService.add({ severity: 'error', summary: 'Failed to Fetch Messages', detail: e?.message || 'An error occurred while fetching diagnostic messages' });
    }
  }

  async ipTableGet(): Promise<any[]> {
    const response = await this.wsService.sendRequest('ipTableGet', undefined, this.getStoredToken());
    if (!environment.production) {
      console.log('IP Table get response:', response);
    }
    return response;
  }

  async cresnetGet(): Promise<CresnetDevice[]> {
    const response = await this.wsService.sendRequest('cresnetGet', undefined, this.getStoredToken());
    if (!environment.production) {
      console.log('Cresnet get response:', response);
    }
    return response;
  }

  private systemMonitorHistoryGet(cachedOnly: boolean = true): Promise<SystemMonitorStat[]> {
    return this.wsService.sendRequest('systemMonitorHistoryGet', { cachedOnly });
  }

  systemMonitorGet7DaySample(): Promise<SystemMonitorStat[]> {
    return this.wsService.sendRequest('systemMonitor7DaySample');
  }

  autodiscoveryGet(force = false): Promise<any> {
    return this.wsService.sendRequest('autodiscoveryGet', force ? { force: true } : undefined, this.getStoredToken());
  }

  loggerStart(): Promise<void> {
    console.debug('[loggerStart] Fetching log history...');
    return this.wsService.sendRequest('loggerGetHistory', undefined, this.getStoredToken())
      .then((response) => {
        console.debug('[loggerStart] Received log history:', response?.length ?? 0, 'entries');
        this.loggerHistory.set(response);
      })
      .catch((e: any) => console.error('[loggerStart] loggerGetHistory error', e));
  }

  registerDevice(registrationCode: string): Promise<void> {
    return this.wsService.sendRequest('registerDevice', { registrationCode })
      .then(() => {
        // Optimistically flag cloud registered so UI updates instantly
        this.statusData.update((sd: any) => {
          if (!sd) return sd;
          return { ...sd, CloudRegistered: true };
        });
        // Fetch authoritative status to capture device id / last check-in fields
        return this.wsService.sendRequest('statusGet')
          .then(r => this.statusData.set(r))
          .catch(e => {
            console.error('post-register statusGet failed', e);
          });
      });
  }

  unregisterDevice(): Promise<void> {
    return this.wsService.sendRequest('unregisterDevice')
      .then(() => {
        // Optimistically clear cloud registration state
        this.statusData.update((sd: any) => {
          if (!sd) return sd;
          const clone = { ...sd };
          clone.CloudRegistered = false;
          clone.CloudDeviceId = null;
          clone.CloudCheckedIn = null;
          return clone;
        });
        // Refresh status to confirm
        return this.wsService.sendRequest('statusGet')
          .then(r => this.statusData.set(r))
          .catch(e => console.error('post-unregister statusGet failed', e));
      });
  }

  forceUnregisterDevice(): Promise<void> {
    return this.wsService.sendRequest('forceUnregisterDevice')
      .then(() => {
        this.statusData.update((sd: any) => {
          if (!sd) return sd;
          const clone = { ...sd };
          clone.CloudRegistered = false;
          clone.CloudDeviceId = null;
          clone.CloudCheckedIn = null;
          return clone;
        });
        return this.wsService.sendRequest('statusGet')
          .then(r => this.statusData.set(r))
          .catch(e => console.error('post-force-unregister statusGet failed', e));
      });
  }

  /** The page to come back to after a restart or reboot, unless already on the reconnect screen. */
  private reconnectReturnUrl(): string {
    const url = this.router.url;
    return url.startsWith('/reconnecting') ? '/' : url;
  }

  async restartApp(): Promise<any> {
    const response = await this.wsService.sendRequest('restartApp', undefined, this.getStoredToken());
    if (!environment.production) {
      console.log('Restart app response:', response);
    }
    // Force disconnect since we know the server is going down
    this.forceDisconnect();
    return response;
  }

  async rebootAppliance(): Promise<any> {
    const response = await this.wsService.sendRequest('rebootAppliance', undefined, this.getStoredToken());
    if (!environment.production) {
      console.log('Reboot appliance response:', response);
    }
    // Force disconnect since we know the server is going down
    this.forceDisconnect();
    return response;
  }

  /**
   * Force disconnect the SignalR connection immediately.
   * Use when you know the server is going down (reboot/restart).
   */
  forceDisconnect() {
    this.wsService.forceDisconnect();
    this.connectionStatus.set('disconnected');
    this.stopStatusAndMessagesPolling();
    this.detachNotificationListener();
  }

  /**
   * Trigger a reconnection attempt.
   * Use when a tab becomes visible and the connection is stale.
   */
  reconnect() {
    if (this.connectionStatus() === 'disconnected') {
      console.log('[ApiService] Manual reconnect triggered');
      this.connectWithRetry();
    }
  }

  // Property List API methods
  async propertyListGetAll(): Promise<{ items: PropertyListItem[]; count: number }> {
    const response = await this.wsService.sendRequest('propertyListGetAll', undefined, this.getStoredToken());
    if (!environment.production) {
      console.log('Property list get all response:', response);
    }
    return response;
  }

  async propertyListSet(key: string, value: any): Promise<{ success: boolean; key: string; value: any }> {
    const response = await this.wsService.sendRequest('propertyListSet', { key, value }, this.getStoredToken());
    if (!environment.production) {
      console.log('Property list set response:', response);
    }
    return response;
  }

  async propertyListDelete(key: string): Promise<{ success: boolean; key: string }> {
    const response = await this.wsService.sendRequest('propertyListDelete', { key }, this.getStoredToken());
    if (!environment.production) {
      console.log('Property list delete response:', response);
    }
    return response;
  }

  // Credentials API methods
  async credentialsGetAll(): Promise<{ items: CredentialItem[]; count: number }> {
    const response = await this.wsService.sendRequest('credentialsGetAll', undefined, this.getStoredToken());
    if (!environment.production) {
      console.log('Credentials get all response:', response);
    }
    return response;
  }

  async credentialsSet(key: string, value: string): Promise<{ success: boolean; key: string; hasValue: boolean; valueLength: number }> {
    const response = await this.wsService.sendRequest('credentialsSet', { key, value }, this.getStoredToken());
    if (!environment.production) {
      console.log('Credentials set response:', response);
    }
    return response;
  }

  async credentialsDelete(key: string): Promise<{ success: boolean; key: string }> {
    const response = await this.wsService.sendRequest('credentialsDelete', { key }, this.getStoredToken());
    if (!environment.production) {
      console.log('Credentials delete response:', response);
    }
    return response;
  }

  async credentialsGetValue(key: string): Promise<{ key: string; value: string }> {
    const response = await this.wsService.sendRequest('credentialsGetValue', { key }, this.getStoredToken());
    if (!environment.production) {
      console.log('Credentials get value response:', response);
    }
    return response;
  }

  // Software Update API methods
  async updateGetCurrentAppInfo(): Promise<any> {
    return this.wsService.sendRequest('updateGetCurrentAppInfo', undefined, this.getStoredToken());
  }

  async updateListAvailable(): Promise<any> {
    return this.wsService.sendRequest('updateListAvailable', undefined, this.getStoredToken());
  }

  async updateGetStaged(): Promise<any> {
    return this.wsService.sendRequest('updateGetStaged', undefined, this.getStoredToken());
  }

  async updateDownloadFromCloud(packageId: string, versionId: string): Promise<any> {
    return this.wsService.sendRequest('updateDownloadFromCloud', { packageId, versionId }, this.getStoredToken());
  }

  /**
   * Upload a CPZ file using HTTP multipart upload (streaming, memory-efficient)
   */
  async updateUploadPackage(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Use relative path - will be proxied in dev, direct in production
      const response = await fetch(`/api/update/upload`, {
        method: 'POST',
        body: formData,
        // Don't set Content-Type header - browser will set it with boundary for multipart
      });

      const result = await response.json();
      return result;
    } catch (error: any) {
      return { success: false, error: error.message || 'Upload failed' };
    }
  }

  async updateApply(stagedPath: string): Promise<any> {
    return this.wsService.sendRequest('updateApply', { stagedPath }, this.getStoredToken());
  }

  async updateRemoveStaged(stagedPath: string): Promise<any> {
    return this.wsService.sendRequest('updateRemoveStaged', { stagedPath }, this.getStoredToken());
  }

  // ============================================================================
  // Firmware Update API
  // ============================================================================

  async firmwareGetCurrent(): Promise<any> {
    return this.wsService.sendRequest('firmwareGetCurrent', undefined, this.getStoredToken());
  }

  async firmwareListAvailable(): Promise<any> {
    return this.wsService.sendRequest('firmwareListAvailable', undefined, this.getStoredToken());
  }

  async firmwareDownload(downloadUrl: string, fileName: string): Promise<any> {
    return this.wsService.sendRequest('firmwareDownload', { downloadUrl, fileName }, this.getStoredToken());
  }

  async firmwareApply(fileName: string): Promise<any> {
    return this.wsService.sendRequest('firmwareApply', { fileName }, this.getStoredToken());
  }

  // ============================================================================
  // File Explorer API
  // ============================================================================

  /**
   * List directory contents within ProgramRootDirectory
   * @param path Relative path from root (empty or "/" for root)
   */
  async fileExplorerListDir(path: string = '/'): Promise<FileExplorerListResponse> {
    return this.wsService.sendRequest('fileExplorerListDir', { path }, this.getStoredToken());
  }

  /**
   * Read file contents for preview
   * @param path Relative path from root
   * @param maxBytes Maximum bytes to read (default 1MB on backend)
   */
  async fileExplorerReadFile(path: string, maxBytes?: number): Promise<FileExplorerReadResponse> {
    return this.wsService.sendRequest('fileExplorerReadFile', { path, maxBytes }, this.getStoredToken());
  }

  /**
   * Get file metadata without reading content
   * @param path Relative path from root
   */
  async fileExplorerGetFileInfo(path: string): Promise<FileExplorerFileInfo> {
    return this.wsService.sendRequest('fileExplorerGetFileInfo', { path }, this.getStoredToken());
  }

  /**
   * Download a file (returns base64 encoded content)
   * @param path Relative path from root
   */
  async fileExplorerDownloadFile(path: string): Promise<FileExplorerReadResponse> {
    return this.wsService.sendRequest('fileExplorerDownloadFile', { path }, this.getStoredToken());
  }

  /**
   * Delete a file
   * @param path Relative path from root
   */
  async fileExplorerDeleteFile(path: string): Promise<{ success: boolean; error?: string; path?: string; message?: string }> {
    return this.wsService.sendRequest('fileExplorerDeleteFile', { path }, this.getStoredToken());
  }

  /**
   * Save/write file content
   * @param path Relative path from root
   * @param content File content to save
   */
  async fileExplorerSaveFile(path: string, content: string): Promise<{ success: boolean; error?: string; path?: string; name?: string; sizeBytes?: number; modifiedUtc?: string; message?: string }> {
    return this.wsService.sendRequest('fileExplorerSaveFile', { path, content }, this.getStoredToken());
  }

  /**
   * Upload a file to a folder
   * @param folderPath Relative folder path from root
   * @param file File to upload
   */
  async fileExplorerUploadFile(folderPath: string, file: File): Promise<{ success: boolean; error?: string; path?: string; name?: string; sizeBytes?: number; extension?: string; message?: string }> {
    const formData = new FormData();
    formData.append('folderPath', folderPath);
    formData.append('file', file);

    // File upload uses direct HTTP POST endpoint (not SignalR)
    return this.httpClient.post<any>('/api/fileExplorer/upload', formData).toPromise() as Promise<any>;
  }

  // ============================================================================
  // System Info API
  // ============================================================================

  /**
   * Get loaded assemblies and their versions
   */
  async assembliesGet(): Promise<AssembliesResponse> {
    return this.wsService.sendRequest('assembliesGet');
  }

  // ============================================================================
  // Changelog API
  // ============================================================================

  /**
   * Get changelog/release notes content
   */
  async changelogGet(): Promise<any> {
    return this.wsService.sendRequest('changelogGet');
  }

  // ============================================================================
  // Devices API
  // ============================================================================

  /**
   * Get list of all registered devices
   */
  async getDevices(): Promise<any[]> {
    return this.wsService.sendRequest('devicesGet');
  }

  /**
   * Get detailed information about a specific device
   * @param id Device ID for a driver, or the uniqueId of a touch panel / CIP device,
   *           which has no runtime device ID
   */
  async getDevice(id: number | string): Promise<any> {
    return typeof id === 'number'
      ? this.wsService.sendRequest('deviceGet', { id })
      : this.wsService.sendRequest('deviceGet', { uniqueId: id });
  }

  /**
   * Enable or disable debug mode for a device
   * @param id Device ID
   * @param enabled Whether to enable debug mode
   */
  async setDeviceDebug(id: number, enabled: boolean): Promise<any> {
    return this.wsService.sendRequest('deviceSetDebug', { id, enabled }, this.getStoredToken());
  }

  /**
   * Get debug log entries for a device
   * @param id Device ID
   */
  async getDeviceDebugLogs(id: number): Promise<any> {
    return this.wsService.sendRequest('deviceDebugLogsGet', { id });
  }

  /**
   * Get available commands for a device
   * @param id Device ID
   */
  async getDeviceCommands(id: number): Promise<{ id: number; name: string; commands: DeviceCommand[] }> {
    return this.wsService.sendRequest('deviceCommandsGet', { id });
  }

  /**
   * Execute a command on a device
   * @param id Device ID
   * @param commandName Name of the command to execute
   * @param args Arguments to pass to the command
   */
  async executeDeviceCommand(id: number, commandName: string, args: Record<string, any> = {}): Promise<DeviceCommandResult> {
    return this.wsService.sendRequest('deviceCommandExecute', { id, commandName, arguments: args }, this.getStoredToken());
  }

  /**
   * Update device asset information (warranty, service contract, etc.)
   * @param id Device ID
   * @param assets Asset information to update
   */
  async updateDeviceAssets(id: number, assets: {
    assetTag?: string | null;
    purchaseDate?: string | null;
    warrantyExpiresUtc?: string | null;
    serviceContractExpiresUtc?: string | null;
    endOfLifeDate?: string | null;
  }): Promise<any> {
    const headers = this.getAuthHeaders();
    return this.httpClient.patch<any>(`${environment.apiBaseUrl}/devices/${id}/assets`, assets, { headers }).toPromise();
  }

  // ============================================================================
  // Remote Console API
  // ============================================================================

  /**
   * Connect to a remote device via SSH or Telnet
   */
  async remoteConsoleConnect(host: string, port: number, protocol: 'ssh' | 'telnet' | 'ctp' | 'securectp', username: string, password: string): Promise<any> {
    return this.wsService.sendRequest('remoteConsoleConnect', { host, port, protocol, username, password }, this.getStoredToken());
  }

  /**
   * Send input data to an existing remote console session
   */
  async remoteConsoleWrite(sessionId: string, data: string): Promise<{ success: boolean }> {
    return this.wsService.sendRequest('remoteConsoleWrite', { sessionId, data }, this.getStoredToken());
  }

  /**
   * Disconnect a remote console session
   */
  async remoteConsoleDisconnect(sessionId: string): Promise<{ success: boolean }> {
    return this.wsService.sendRequest('remoteConsoleDisconnect', { sessionId }, this.getStoredToken());
  }

  /**
   * Get status of a remote console session
   */
  async remoteConsoleGetStatus(sessionId: string): Promise<any> {
    return this.wsService.sendRequest('remoteConsoleGetStatus', { sessionId }, this.getStoredToken());
  }

  /**
   * List all active remote console sessions
   */
  async remoteConsoleListSessions(): Promise<any[]> {
    return this.wsService.sendRequest('remoteConsoleListSessions', undefined, this.getStoredToken());
  }

  /**
   * Accept or reject a pending SSH host key
   */
  async remoteConsoleAcceptHostKey(sessionId: string, accept: boolean): Promise<{ success: boolean }> {
    return this.wsService.sendRequest('remoteConsoleAcceptHostKey', { sessionId, accept }, this.getStoredToken());
  }

  // ============================================================================
  // Network Diagnostics
  // ============================================================================

  /**
   * Run a network reachability test from this processor: ICMP ping, plus an optional
   * TCP port scan and DNS resolution. Bounds are enforced device-side.
   */
  async networkPing(request: PingRequest): Promise<PingResult> {
    return this.wsService.sendRequest(
      'networkPing',
      {
        host: request.host,
        count: request.count ?? PING_LIMITS.defaultCount,
        timeoutMs: request.timeoutMs ?? PING_LIMITS.defaultTimeoutMs,
        port: request.port ?? 0,
        ports: request.ports ?? '',
        portTimeoutMs: request.portTimeoutMs ?? PING_LIMITS.defaultPortTimeoutMs,
        resolveDns: request.resolveDns ?? true
      },
      this.getStoredToken()
    );
  }

  // ============================================================================
  // BACnet
  // ============================================================================

  /** BACnet status, this program's host objects and any bound remote devices. */
  async bacnetStatusGet(): Promise<BacnetStatusResponse> {
    return this.wsService.sendRequest('bacnetStatusGet', undefined, this.getStoredToken());
  }

  /** Current discovery state and whatever the last discovery found. */
  async bacnetDiscoveryGet(): Promise<BacnetDiscoveryResponse> {
    return this.wsService.sendRequest('bacnetDiscoveryGet', undefined, this.getStoredToken());
  }

  /** Starts a device discovery. This broadcasts Who-Is onto the local network. */
  async bacnetDiscoveryStart(timeoutMinutes: number): Promise<BacnetCommandResult> {
    return this.wsService.sendRequest('bacnetDiscoveryStart', { timeoutMinutes }, this.getStoredToken());
  }

  /** Switches on discovery in the processor's BACnet stack. Processor-wide; does not start a discovery. */
  async bacnetDiscoveryEnable(): Promise<BacnetCommandResult> {
    return this.wsService.sendRequest('bacnetDiscoveryEnable', undefined, this.getStoredToken());
  }

  async bacnetDiscoveryStop(): Promise<BacnetCommandResult> {
    return this.wsService.sendRequest('bacnetDiscoveryStop', undefined, this.getStoredToken());
  }

  /** Starts object discovery against one device found by device discovery. */
  async bacnetObjectDiscoveryStart(deviceId: number, timeoutMinutes: number): Promise<BacnetCommandResult> {
    return this.wsService.sendRequest('bacnetObjectDiscoveryStart', { deviceId, timeoutMinutes }, this.getStoredToken());
  }

  async bacnetObjectDiscoveryStop(): Promise<BacnetCommandResult> {
    return this.wsService.sendRequest('bacnetObjectDiscoveryStop', undefined, this.getStoredToken());
  }

  /** System key and hostname, which Crestron requires to issue a BACnet licence. */
  async bacnetLicenseInfoGet(): Promise<BacnetLicenseInfo> {
    return this.wsService.sendRequest('bacnetLicenseInfoGet', undefined, this.getStoredToken());
  }

  /** Installs a BACnet licence key. The processor must be rebooted before it takes effect. */
  async bacnetLicenseApply(licenseKey: string): Promise<BacnetLicenseApplyResult> {
    return this.wsService.sendRequest('bacnetLicenseApply', { licenseKey }, this.getStoredToken());
  }

  /** BBMD / foreign device configuration, live BDT and FDT, and the change in progress. */
  async bacnetNetworkGet(): Promise<BacnetNetworkResponse> {
    return this.wsService.sendRequest('bacnetNetworkGet', undefined, this.getStoredToken());
  }

  /** Validates a configuration and lists the steps and restarts it needs. Changes nothing. */
  async bacnetNetworkPreview(request: BacnetNetworkRequest): Promise<BacnetNetworkPreview> {
    return this.wsService.sendRequest('bacnetNetworkPreview', { request }, this.getStoredToken());
  }

  /** Applies a configuration. Processor-wide; restarts the program one or more times. */
  async bacnetNetworkApply(request: BacnetNetworkRequest): Promise<BacnetNetworkApplyResult> {
    return this.wsService.sendRequest('bacnetNetworkApply', { request }, this.getStoredToken());
  }

  /** Clears the last result, or stops an in-progress change from advancing. */
  async bacnetNetworkDismiss(): Promise<BacnetCommandResult> {
    return this.wsService.sendRequest('bacnetNetworkDismiss', undefined, this.getStoredToken());
  }

  // ============================================================================
  // Power Distribution (PDUs)
  // ============================================================================

  async powerDevicesGet(): Promise<PowerDeviceDto[]> {
    return this.wsService.sendRequest('powerDevicesGet', undefined, this.getStoredToken());
  }

  async powerOutletAction(id: number, outletId: number, action: OutletAction): Promise<OutletActionResult> {
    return this.wsService.sendRequest('powerOutletAction', { id, outletId, action }, this.getStoredToken());
  }

  async powerHistoryGet(id: number): Promise<PowerHistorySample[]> {
    return this.wsService.sendRequest('powerHistoryGet', { id }, this.getStoredToken());
  }

  /**
   * Room ratings on/off on this processor (Room ratings page). While the processor is cloud managed the cloud
   * portal's setting applies and `managedByCloud` is true; the setters then fail.
   */
  async feedbackSettingsGet(): Promise<DeviceFeedbackSettings> {
    return this.wsService.sendRequest('feedbackSettingsGet', undefined, this.getStoredToken());
  }

  /** Turn ratings on or off for the system and every room without its own setting. */
  async feedbackSettingsSet(enabled: boolean): Promise<DeviceFeedbackSettings> {
    return this.wsService.sendRequest('feedbackSettingsSet', { enabled }, this.getStoredToken());
  }

  /** Turn ratings on or off for one room, or with null let it follow the default again. */
  async feedbackRoomSettingSet(roomId: number, enabled: boolean | null): Promise<DeviceFeedbackSettings> {
    return this.wsService.sendRequest('feedbackRoomSettingSet', { roomId, enabled }, this.getStoredToken());
  }

  /** Rooms with occupancy sensors or a booking source (Room Usage page). Needs a signed-in user. */
  async roomUsageGet(): Promise<RoomUsage[]> {
    return this.wsService.sendRequest('roomUsageGet', undefined, this.getStoredToken());
  }

  /** Ask a room's booking source to re-read its calendar; returns the room's updated usage. */
  async roomBookingsRefresh(roomId: number): Promise<RoomUsage> {
    return this.wsService.sendRequest('roomBookingsRefresh', { roomId }, this.getStoredToken());
  }

  /** Sensors page: MQTT state, Shelly devices on this processor, devices announcing on the broker and the rooms. */
  async sensorsGet(): Promise<SensorsState> {
    return this.wsService.sendRequest('sensorsGet', undefined, this.getStoredToken());
  }

  /** MQTT tool: the connection and the messages after `after` in capture `captureId`. The first call starts capturing. */
  async mqttMonitorGet(captureId: string | null, after: number): Promise<Omit<MqttMonitorResponse, 'messagesAllowed'>> {
    return this.wsService.sendRequest('mqttMonitorGet', { captureId, after }, this.getStoredToken());
  }

  /** Ask every device on the broker to announce itself again. */
  async sensorsDiscover(): Promise<{ sent: boolean }> {
    return this.wsService.sendRequest('sensorsDiscover', undefined, this.getStoredToken());
  }

  /** Adopt a device seen on the broker; it starts when the program restarts. */
  async shellyAdopt(request: ShellyDeviceRequest): Promise<SensorsState> {
    return this.wsService.sendRequest('shellyAdopt', request, this.getStoredToken());
  }

  /** Change an adopted device; applies when the program restarts. */
  async shellyUpdate(request: ShellyDeviceRequest): Promise<SensorsState> {
    return this.wsService.sendRequest('shellyUpdate', request, this.getStoredToken());
  }

  /** Forget an adopted device; it stops when the program restarts. */
  async shellyRemove(deviceId: string): Promise<SensorsState> {
    return this.wsService.sendRequest('shellyRemove', { deviceId }, this.getStoredToken());
  }

  /** Point a device at the processor's embedded broker over its local HTTP interface, and adopt it. */
  async shellyPair(request: ShellyPairRequest): Promise<{ paired: string; sensors: SensorsState }> {
    return this.wsService.sendRequest('shellyPair', request, this.getStoredToken());
  }

  ngOnDestroy() {
    if (this.reconnectSub) {
      this.reconnectSub.unsubscribe();
    }
    this.stopStatusAndMessagesPolling();
    this.detachNotificationListener();
  }
}